import { gql, useMutation } from '@apollo/client';
import { useToast } from 'components/ui/toast';
import { Mutation, MutationFileFolderChangeArgs } from 'middleware-types';
import { handleNoResponse, responseHasErrors } from 'utils/errors';
import { useDocumentSearchContext } from '../../components/document-search-provider';
import { useDocumentsContext } from '../../documents-page';
import { updateFolderContents } from '../../utils/cache-helpers';

const CHANGE_FILE_FOLDERS = gql`
	mutation MoveFile($request: ChangeFileFolderRequest!) {
		fileFolderChange(request: $request) {
			id
			folder {
				id
			}
		}
	}
`;

export const useMoveFiles = () => {
	const toast = useToast();
	const { entityType, entityId } = useDocumentsContext();
	const { searchValue } = useDocumentSearchContext();

	const [_moveFiles, { loading }] = useMutation<
		Pick<Mutation, 'fileFolderChange'>,
		MutationFileFolderChangeArgs
	>(CHANGE_FILE_FOLDERS);

	const moveFiles = async (fileIds: string[], oldFolderId?: string, newFolderId?: string) => {
		return await _moveFiles({
			variables: { request: { fileIds: fileIds, folderId: newFolderId } },
			update: (cache, { data }) => {
				if (!data) return;
				updateFolderContents(
					{ cache, folderId: oldFolderId, entityType, entityId, searchValue },
					(existing) => ({
						...existing,
						files: existing.files?.filter((f) => !fileIds.includes(f.id)),
					})
				);
			},
			onError: (e) => console.log(JSON.stringify(e)),
		})
			.then((res) => {
				if (responseHasErrors(res.errors, { toast })) {
					return false;
				}
				fileIds.length > 1
					? toast.push('Files moved successfully.', { variant: 'success' })
					: toast.push('File moved successfully.', { variant: 'success' });
				return true;
			})
			.catch(() => {
				handleNoResponse({ toast });
				return false;
			});
	};

	return { moveFiles, loading };
};
